import { createContext, useCallback, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

const DEFAULT_YEAR = new Date().getFullYear()
const DEFAULT_VIEW = 'probability_weighted'

interface AppChromeState {
  year: number
  viewCode: string
  setYear: (year: number) => void
  setViewCode: (code: string) => void
}

const AppChromeContext = createContext<AppChromeState | null>(null)

export function AppChromeProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useSearchParams()

  const year = Number(params.get('year') ?? DEFAULT_YEAR)
  const viewCode = params.get('view') ?? DEFAULT_VIEW

  const setYear = useCallback(
    (next: number) => {
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev)
          copy.set('year', String(next))
          return copy
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const setViewCode = useCallback(
    (code: string) => {
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev)
          copy.set('view', code)
          return copy
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const value = useMemo(
    () => ({
      year: Number.isFinite(year) ? year : DEFAULT_YEAR,
      viewCode,
      setYear,
      setViewCode,
    }),
    [year, viewCode, setYear, setViewCode],
  )

  return <AppChromeContext.Provider value={value}>{children}</AppChromeContext.Provider>
}

// Hook co-located with provider; separate file would only re-export this.
// eslint-disable-next-line react-refresh/only-export-components -- intentional context hook export
export function useAppChrome(): AppChromeState {
  const ctx = useContext(AppChromeContext)
  if (!ctx) {
    throw new Error('useAppChrome must be used within AppChromeProvider')
  }
  return ctx
}
